import React, { useState, useRef, useEffect } from 'react';
import { CentralEdgeDevice } from '@simcheck/shared-types';
import { StatusBadge } from './StatusBadge';
import { ArrowLeft, ArrowLeftRight, RefreshCw, FlaskConical, Pencil, Check, X, Power, RotateCcw, Zap, Loader2, Wifi, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { edgeDeviceService } from '@/services/edgeDeviceService';
import { useToast } from '@/contexts/ToastContext';
import { SIMCHECK_ENV } from '@/config/env';

const ENV_LABEL: Record<string, string> = { prod: 'production', staging: 'staging' };
const envLabel = (env?: string) => (env ? ENV_LABEL[env] || env : 'another environment');

interface DeviceHeaderProps {
  device: CentralEdgeDevice;
  onRefresh?: () => void;
  onDeviceUpdate?: (device: CentralEdgeDevice) => void;
  refreshing?: boolean;
}

export const DeviceHeader: React.FC<DeviceHeaderProps> = ({
  device,
  onRefresh,
  onDeviceUpdate,
  refreshing = false
}) => {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(device.name);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const handleSave = async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === device.name) {
      setEditing(false);
      setEditName(device.name);
      return;
    }

    try {
      setSaving(true);
      await edgeDeviceService.updateDeviceSettings(device.id, { name: trimmed });
      const updated = { ...device, name: trimmed };
      onDeviceUpdate?.(updated);
      setEditing(false);
    } catch (err) {
      console.error('Failed to update device name:', err);
      setEditName(device.name);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditName(device.name);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') handleCancel();
  };

  const [confirmAction, setConfirmAction] = useState<'reboot' | 'restart_gateway' | 'force_reboot' | 'enter_wifi_setup' | 'switch_environment' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const isDraining = device.maintenanceState === 'draining';
  const isParked = device.maintenanceState === 'parked';
  const isOnline = device.status === 'online' || (device.status as string) === 'connected';

  // Move between prod and staging. Prod can enroll a Pi into staging the first
  // time; staging can only send a Pi back to prod credentials it already holds.
  const moveTarget: 'prod' | 'staging' = SIMCHECK_ENV === 'prod' ? 'staging' : 'prod';
  const moveBlockedReason = !isOnline
    ? 'The device has to be online to move'
    : isParked || isDraining
    ? 'The device is busy'
    : !Array.isArray(device.envProfiles)
    ? 'This device runs an edge release without environment switching'
    : moveTarget === 'prod' && !device.envProfiles.includes('prod')
    ? 'This device was never on production; bootstrap it there once'
    : null;
  const lastMove = device.envSwitchLast;
  const lastMoveFailed = !!lastMove && lastMove.outcome !== 'ok' && lastMove.from === SIMCHECK_ENV;
  const { info, error: toastError } = useToast();
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
  }, []);

  const handleSwitchEnvironment = async () => {
    try {
      setActionLoading(true);
      await edgeDeviceService.switchEnvironment(device.id, moveTarget);
      setConfirmAction(null);
      info(
        `It finishes any running tests, then moves. If it can't connect to ${envLabel(moveTarget)} it returns here on its own.`,
        `Moving to ${envLabel(moveTarget)}`
      );
      setTimeout(() => onRefresh?.(), 5000);
    } catch (err) {
      console.error('Environment switch failed:', err);
      toastError(err instanceof Error ? err.message : 'Failed to move the device');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSystemAction = async (action: 'reboot' | 'restart_gateway' | 'force_reboot' | 'enter_wifi_setup' | 'switch_environment') => {
    if (action === 'switch_environment') return handleSwitchEnvironment();
    try {
      setActionLoading(true);
      await edgeDeviceService.sendSystemAction(device.id, action);
      setConfirmAction(null);
      if (action === 'enter_wifi_setup') {
        info('The setup hotspot should appear within ~15 seconds; the WiFi status below will update shortly.', 'WiFi setup mode requested');
        // Poll for ~60s so the WiFi chip flips live (status relays via the
        // device's 2s status forwarder once the AP is up).
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        let polls = 0;
        pollTimerRef.current = setInterval(() => {
          onRefresh?.();
          if (++polls >= 12 && pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
        }, 5000);
      } else {
        // Refresh after a delay to see status change
        setTimeout(() => onRefresh?.(), 5000);
      }
    } catch (err) {
      console.error('System action failed:', err);
      toastError(err instanceof Error ? err.message : 'Failed to send action');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="flex justify-between items-center mb-6">
      <div className="flex items-center gap-4">
        <Link
          to="/admin/edge-devices"
          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
          title="Back to devices"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>

        <div>
          <div className="flex items-center gap-3">
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={saving}
                  className="text-2xl font-bold text-foreground bg-transparent border-b-2 border-primary outline-none px-0 py-0"
                />
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="p-1 text-emerald-500 hover:bg-emerald-500/10 rounded transition-colors"
                  title="Save"
                >
                  <Check className="w-5 h-5" />
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="p-1 text-muted-foreground hover:bg-muted rounded transition-colors"
                  title="Cancel"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 group">
                <h1 className="text-2xl font-bold text-foreground">{device.name}</h1>
                <button
                  onClick={() => setEditing(true)}
                  className="p-1 text-muted-foreground/50 hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Edit name"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
            )}
            <StatusBadge status={device.status} />
            {isDraining && (
              <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-500 border-amber-500/30">
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Draining…
              </Badge>
            )}
            {isParked && (
              <Badge variant="outline" className="text-xs bg-sky-500/10 text-sky-500 border-sky-500/30">
                <ArrowLeftRight className="w-3 h-3 mr-1" />
                {isOnline ? `Moving to ${envLabel(device.parkedIn)}…` : `Moved to ${envLabel(device.parkedIn)}`}
              </Badge>
            )}
            {!isParked && lastMoveFailed && (
              <Badge
                variant="outline"
                className="text-xs bg-amber-500/10 text-amber-500 border-amber-500/30"
                title={`${lastMove!.message} (${lastMove!.at})`}
              >
                <AlertTriangle className="w-3 h-3 mr-1" />
                Move to {envLabel(lastMove!.target)} {lastMove!.outcome === 'rolled_back' ? 'rolled back' : 'failed'}
              </Badge>
            )}
            {device.isDemo && (
              <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-500 border-purple-500/30">
                <FlaskConical className="w-3 h-3 mr-1" />
                Demo Device
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {device.deviceId || device.device_id}
            {device.isDemo && (
              <span className="ml-2 text-purple-500/70">(Simulated for demonstration)</span>
            )}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setConfirmAction('restart_gateway')}
          className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded hover:bg-muted transition-colors text-sm"
          title="Restart modem-gateway service (drains in-flight tests first)"
        >
          <RotateCcw className="w-4 h-4" />
          <span>Restart Service</span>
        </button>
        <button
          onClick={() => setConfirmAction('enter_wifi_setup')}
          className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded hover:bg-muted transition-colors text-sm"
          title="Put the device into WiFi hotspot setup mode"
        >
          <Wifi className="w-4 h-4" />
          <span>WiFi Setup</span>
        </button>
        <button
          onClick={() => setConfirmAction('switch_environment')}
          disabled={!!moveBlockedReason}
          className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded hover:bg-muted transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          title={moveBlockedReason || `Move this device to ${envLabel(moveTarget)} (drains in-flight tests first)`}
        >
          <ArrowLeftRight className="w-4 h-4" />
          <span>Move to {envLabel(moveTarget)}</span>
        </button>
        <button
          onClick={() => setConfirmAction('reboot')}
          className="flex items-center gap-2 px-3 py-2 bg-card border border-amber-500/30 text-amber-500 rounded hover:bg-amber-500/10 transition-colors text-sm"
          title="Reboot the device gracefully (drains in-flight tests first)"
        >
          <Power className="w-4 h-4" />
          <span>Reboot</span>
        </button>
        <button
          onClick={() => setConfirmAction('force_reboot')}
          className="flex items-center gap-2 px-3 py-2 bg-card border border-destructive/30 text-destructive rounded hover:bg-destructive/10 transition-colors text-sm"
          title="Force reboot immediately, killing any in-flight tests (no drain)"
        >
          <Zap className="w-4 h-4" />
          <span>Force Reboot</span>
        </button>
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded hover:bg-muted transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {confirmAction === 'reboot'
                ? 'Reboot Device (graceful)'
                : confirmAction === 'force_reboot'
                ? 'Force Reboot Device'
                : confirmAction === 'enter_wifi_setup'
                ? 'Enter WiFi Setup Mode'
                : confirmAction === 'switch_environment'
                ? `Move to ${envLabel(moveTarget)}`
                : 'Restart Service (graceful)'}
            </h3>
            <p className="text-muted-foreground mb-4">
              {confirmAction === 'reboot'
                ? `Reboot "${device.name}"? It will first finish any in-flight tests (up to ~15 min), stop accepting new tests, then reboot. The device will be offline for 1-2 minutes.`
                : confirmAction === 'force_reboot'
                ? `Force reboot "${device.name}" immediately? Any in-flight tests will be killed and their executions may be marked failed. Use this only when the device is wedged.`
                : confirmAction === 'enter_wifi_setup'
                ? `Put "${device.name}" into WiFi hotspot setup mode for up to 15 minutes? Its setup hotspot will appear nearby; if the device's uplink is WiFi, it will go offline until setup completes.`
                : confirmAction === 'switch_environment'
                ? `Move "${device.name}" to ${envLabel(moveTarget)}? It finishes any in-flight tests (up to ~15 min), then connects to ${envLabel(moveTarget)} and shows as moved here, with no offline alerts. It keeps its ${envLabel(SIMCHECK_ENV)} credentials, so it can be moved back. If it can't connect to ${envLabel(moveTarget)} within a few minutes, it comes back here on its own.`
                : `Restart the modem-gateway service on "${device.name}"? It will first finish any in-flight tests (up to ~15 min) before restarting.`
              }
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmAction(null)}
                disabled={actionLoading}
                className="px-4 py-2 bg-card border border-border rounded hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSystemAction(confirmAction)}
                disabled={actionLoading}
                className={`px-4 py-2 rounded text-white transition-colors ${
                  confirmAction === 'force_reboot'
                    ? 'bg-destructive hover:bg-destructive/90'
                    : confirmAction === 'reboot'
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-primary hover:bg-primary/90'
                }`}
              >
                {actionLoading
                  ? 'Sending...'
                  : confirmAction === 'force_reboot'
                  ? 'Force Reboot'
                  : confirmAction === 'reboot'
                  ? 'Reboot'
                  : confirmAction === 'enter_wifi_setup'
                  ? 'Start Setup Mode'
                  : confirmAction === 'switch_environment'
                  ? `Move to ${envLabel(moveTarget)}`
                  : 'Restart'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
